<template>
  <div class="agent-detail-container">
    <!-- Top Navigation Bar -->
    <nav class="top-nav">
      <div class="nav-content">
        <div class="nav-left">
          <el-button circle class="back-btn" @click="$router.push('/agents')">
            <el-icon><ArrowLeft /></el-icon>
          </el-button>
          <div class="logo">
            <el-icon :size="28" color="#409EFF"><Cpu /></el-icon>
            <span class="logo-text">{{ agentDisplayName }}</span>
          </div>
        </div>
        <div class="nav-right">
          <el-button size="small" @click="router.push('/dashboard')">工作台</el-button>
          <el-button 
            :type="agent?.is_available ? 'warning' : 'success'" 
            size="small"
            @click="toggleAgentStatus"
          >
            {{ agent?.is_available ? '停用' : '启用' }}
          </el-button>
          <el-button size="small" plain @click="handleLogout">退出登录</el-button>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-wrapper">
        <el-row :gutter="24">
          <!-- Left Column: Agent Info & Controls -->
          <el-col :span="8">
            <el-card class="info-card" shadow="hover">
              <template #header>
                <div class="card-header">
                  <span>基本信息</span>
                  <el-tag :type="statusType" effect="dark">{{ statusText }}</el-tag>
                </div>
              </template>
              
              <el-descriptions :column="1" border>
                <el-descriptions-item label="智能体名称">{{ agent?.agent_name }}</el-descriptions-item>
                <el-descriptions-item label="类型">{{ agent?.agent_type }}</el-descriptions-item>
                <el-descriptions-item label="版本">{{ agent?.version }}</el-descriptions-item>
                <el-descriptions-item label="状态">
                  <el-tag :type="statusType" size="small">{{ statusText }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="最后活跃">{{ formatTime(agent?.last_active) }}</el-descriptions-item>
                <el-descriptions-item label="运行时长">{{ formatUptime(agent?.uptime_seconds) }}</el-descriptions-item>
              </el-descriptions>

              <div class="stats-grid">
                <div class="stat-item">
                  <div class="stat-value">{{ agent?.tasks_completed || 0 }}</div>
                  <div class="stat-label">完成任务</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">{{ agent?.tasks_failed || 0 }}</div>
                  <div class="stat-label">失败任务</div>
                </div>
                <div class="stat-item">
                  <div class="stat-value">{{ successRate }}%</div>
                  <div class="stat-label">成功率</div>
                </div>
              </div>
            </el-card>

            <el-card class="control-card" shadow="hover" style="margin-top: 24px;">
              <template #header>
                <span>控制面板</span>
              </template>
              
              <div class="control-buttons">
                <el-button type="primary" block @click="viewCurrentTask">
                  <el-icon><Document /></el-icon>
                  查看当前任务
                </el-button>
                <el-button type="success" block @click="assignNewTask">
                  <el-icon><Plus /></el-icon>
                  分配新任务
                </el-button>
                <el-button type="warning" block @click="viewLogs">
                  <el-icon><DocumentCopy /></el-icon>
                  查看日志
                </el-button>
                <el-button type="danger" plain block @click="resetAgent">
                  <el-icon><RefreshRight /></el-icon>
                  重置智能体
                </el-button>
              </div>
            </el-card>
          </el-col>

          <!-- Right Column: Logs & Activity -->
          <el-col :span="16">
            <el-card class="logs-card" shadow="hover">
              <template #header>
                <div class="card-header-actions">
                  <span>最近日志</span>
                  <el-button size="small" @click="loadLogs">
                    <el-icon><Refresh /></el-icon>
                    刷新
                  </el-button>
                </div>
              </template>
              
              <el-table :data="logs" stripe v-loading="loadingLogs" height="500">
                <el-table-column prop="timestamp" label="时间" width="180">
                  <template #default="{ row }">
                    {{ formatTimestamp(row.timestamp) }}
                  </template>
                </el-table-column>
                <el-table-column prop="level" label="级别" width="100">
                  <template #default="{ row }">
                    <el-tag :type="getLogLevelType(row.level)" size="small">{{ row.level }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="message" label="消息" />
              </el-table>
            </el-card>

            <el-card class="workflow-card" shadow="hover" style="margin-top: 24px;">
              <template #header>
                <span>工作流程位置</span>
              </template>
              
              <div class="workflow-steps">
                <el-steps direction="vertical" :active="currentStep" finish-status="success">
                  <el-step title="需求分析" description="AI-01智能体处理" />
                  <el-step title="剧本创作" description="AI-02智能体处理" />
                  <el-step title="剧本审核" description="AI-03智能体处理" />
                  <el-step title="场景分解" description="AI-04智能体处理" />
                  <el-step title="视觉制作" description="AI-05、AI-06智能体协作" />
                  <el-step title="音视频合成" description="AI-07、AI-08智能体协作" />
                  <el-step title="合规审查" description="AI-09智能体终审" />
                </el-steps>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { agentsApi } from '@/api/agents'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import {
  Cpu,
  ArrowLeft,
  Document,
  Plus,
  DocumentCopy,
  RefreshRight,
  Refresh,
} from '@element-plus/icons-vue'
import type { AgentInfo } from '@/types/workflow'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const agentName = computed(() => route.params.agentName as string)
const agent = ref<AgentInfo | null>(null)
const loading = ref(false)
const loadingLogs = ref(false)
const logs = ref<any[]>([])
const currentStep = ref(0)

// Agent display name mapping
const agentDisplayNames: Record<string, string> = {
  orchestrator: '编排智能体 · Orchestrator',
  demand_analysis: '需求分析智能体 · AI-01',
  script_generation: '剧本创作智能体 · AI-02',
  script_review: '剧本审核智能体 · AI-03',
  scene_breakdown: '场景分解智能体 · AI-04',
  character_design: '角色设计智能体 · AI-05',
  background_generation: '背景生成智能体 · AI-06',
  video_synthesis: '视频合成智能体 · AI-07',
  audio_generation: '音频生成智能体 · AI-08',
  compliance_check: '合规审查智能体 · AI-09',
}

const agentDisplayName = computed(() => {
  return agentDisplayNames[agentName.value] || agentName.value
})

const statusType = computed(() => {
  if (!agent.value) return 'info'
  if (!agent.value.is_available) return 'danger'
  if (agent.value.current_task_id) return 'warning'
  return 'success'
})

const statusText = computed(() => {
  if (!agent.value) return 'Unknown'
  if (!agent.value.is_available) return '离线'
  if (agent.value.current_task_id) return '工作中'
  return '空闲'
})

const successRate = computed(() => {
  if (!agent.value) return 0
  const total = agent.value.tasks_completed + agent.value.tasks_failed
  if (total === 0) return 0
  return Math.round((agent.value.tasks_completed / total) * 100)
})

// Methods
const loadAgent = async () => {
  loading.value = true
  try {
    const data = await agentsApi.getOne(agentName.value)
    agent.value = data
    
    // Set current step based on agent
    const stepMap: Record<string, number> = {
      orchestrator: -1,
      demand_analysis: 0,
      script_generation: 1,
      script_review: 2,
      scene_breakdown: 3,
      character_design: 4,
      background_generation: 4,
      video_synthesis: 5,
      audio_generation: 5,
      compliance_check: 6,
    }
    currentStep.value = stepMap[agentName.value] ?? 0
  } catch (error) {
    console.error('Failed to load agent:', error)
    ElMessage.error('加载智能体信息失败')
  } finally {
    loading.value = false
  }
}

const loadLogs = async () => {
  loadingLogs.value = true
  try {
    const data = await agentsApi.getLogs(agentName.value, 50)
    logs.value = data.logs || []
  } catch (error) {
    console.error('Failed to load logs:', error)
    logs.value = []
  } finally {
    loadingLogs.value = false
  }
}

const toggleAgentStatus = async () => {
  try {
    if (agent.value?.is_available) {
      await agentsApi.deactivate(agentName.value)
      ElMessage.success('智能体已停用')
    } else {
      await agentsApi.activate(agentName.value)
      ElMessage.success('智能体已启用')
    }
    await loadAgent()
  } catch (error) {
    console.error('Failed to toggle agent status:', error)
    ElMessage.error('操作失败')
  }
}

const viewCurrentTask = () => {
  if (agent.value?.current_task_id) {
    ElMessage.info(`查看任务 #${agent.value.current_task_id}`)
  } else {
    ElMessage.info('当前没有执行中的任务')
  }
}

const assignNewTask = () => {
  ElMessageBox.prompt('请输入任务描述', '分配新任务', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputType: 'textarea',
  }).then(async ({ value }) => {
    if (value) {
      ElMessage.success('任务已分配（演示功能，实际需对接后端）')
    }
  }).catch(() => {})
}

const viewLogs = () => {
  loadLogs()
  ElMessage.info('日志已刷新')
}

const resetAgent = async () => {
  try {
    await ElMessageBox.confirm('确定要重置该智能体吗？这将清除所有临时状态。', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })
    
    ElMessage.success('智能体重置成功（演示功能）')
    await loadAgent()
  } catch (error) {
    // User cancelled
  }
}

const handleLogout = () => {
  userStore.logout()
  ElMessage.success('已退出登录')
  router.push('/login')
}

const formatTime = (dateString?: string) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('zh-CN')
}

const formatTimestamp = (timestamp: string) => {
  return new Date(timestamp).toLocaleString('zh-CN')
}

const formatUptime = (seconds?: number) => {
  if (!seconds && seconds !== 0) return '-'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hours}h ${minutes}m ${secs}s`
}

const getLogLevelType = (level: string) => {
  const types: Record<string, any> = {
    INFO: 'info',
    DEBUG: 'info',
    WARNING: 'warning',
    ERROR: 'danger',
    CRITICAL: 'danger',
  }
  return types[level] || 'info'
}

onMounted(() => {
  loadAgent()
  loadLogs()
})
</script>

<style scoped lang="scss">
.agent-detail-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.top-nav {
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  
  .nav-content {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 70px;
  }
  
  .nav-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  .back-btn {
    border: none;
    background: #f5f7fa;
    
    &:hover {
      background: #e4e7ed;
    }
  }
  
  .logo {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .logo-text {
      font-size: 20px;
      font-weight: 700;
      color: #303133;
    }
  }
}

.main-content {
  padding: 32px 24px;
  max-width: 1600px;
  margin: 0 auto;
}

.content-wrapper {
  .info-card, .control-card, .logs-card, .workflow-card {
    border-radius: 12px;
    
    :deep(.el-card__header) {
      background: #f5f7fa;
      border-bottom: none;
      padding: 16px 20px;
    }
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .card-header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #EBEEF5;
    
    .stat-item {
      text-align: center;
      
      .stat-value {
        font-size: 28px;
        font-weight: 700;
        color: #409EFF;
        margin-bottom: 4px;
      }
      
      .stat-label {
        font-size: 13px;
        color: #909399;
      }
    }
  }
  
  .control-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .workflow-steps {
    padding: 16px 0;
  }
}
</style>
